import clsx from 'clsx'
import { Text } from '@/common/components/text'
import type { ConditionType } from '../../api/properties'
import { formatSystemYear } from '../../utils/systemAges'
import * as styles from './PropertyConditionSection.css'

/**
 * Just the fields this section reads: the six condition ratings with their
 * notes, and the system ages with the build year they are read against.
 *
 * Narrowed from PropertyResponse on purpose: the partner offer page shows the
 * same conditions from a deliberately smaller payload, and asking it for a whole
 * PropertyResponse would mean fabricating fields a partner must never receive.
 */
type Rating = ConditionType | null | undefined
type Notes = string | null | undefined

export interface PropertyConditionFields {
  overall_condition?: Rating
  overall_condition_notes?: Notes
  hvac_condition?: Rating
  hvac_condition_notes?: Notes
  roof_condition?: Rating
  roof_condition_notes?: Notes
  plumbing_condition?: Rating
  plumbing_condition_notes?: Notes
  electrical_condition?: Rating
  electrical_condition_notes?: Notes
  foundation_condition?: Rating
  foundation_condition_notes?: Notes
  year_built?: number | null
  hvac_install_year?: number | null
  hvac_install_year_unknown?: boolean
  roof_replacement_year?: number | null
  roof_replacement_year_unknown?: boolean
  water_heater_install_year?: number | null
  water_heater_install_year_unknown?: boolean
}

interface PropertyConditionSectionProps {
  property: PropertyConditionFields
  className?: string
}

interface ConditionItemProps {
  label: string
  condition?: ConditionType | null
  notes?: string | null
}

interface SystemAgeItemProps {
  label: string
  year?: number | null
  unknown?: boolean
  yearBuilt?: number | null
  /** Rating from before system ages existed, shown when no year was collected */
  legacyCondition?: ConditionType | null
  legacyNotes?: string | null
}

/**
 * Format condition type for display (e.g., "good" -> "Good Condition")
 */
function formatCondition(condition: ConditionType): string {
  return `${condition.charAt(0).toUpperCase()}${condition.slice(1)} Condition`
}

/**
 * Single condition item with label, condition text, and optional notes
 */
function ConditionItem({ label, condition, notes }: ConditionItemProps) {
  const displayCondition = condition ?? 'unknown'
  return (
    <div className={styles.conditionItem}>
      <Text as="span" variant="label" color="muted" className={styles.conditionLabel}>{label}</Text>
      <Text as="span" variant="body" weight="medium" className={styles.conditionValue}>{formatCondition(displayCondition)}</Text>
      {notes && <Text variant="bodySm" color="muted" className={styles.conditionNotes}>{notes}</Text>}
    </div>
  )
}

/**
 * HVAC / roof / water heater, most informative first: year + age, the legacy rating on older
 * properties, then "Unknown". Renders nothing when there's none of these.
 */
function SystemAgeItem({ label, year, unknown, yearBuilt, legacyCondition, legacyNotes }: SystemAgeItemProps) {
  if (!year) {
    if (legacyCondition) return <ConditionItem label={label} condition={legacyCondition} notes={legacyNotes} />
    if (!unknown) return null
  }
  return (
    <div className={styles.conditionItem}>
      <Text as="span" variant="label" color="muted" className={styles.conditionLabel}>{label}</Text>
      <Text as="span" variant="body" weight="medium" className={styles.systemValue}>
        {year ? formatSystemYear(year, yearBuilt) : 'Unknown'}
      </Text>
    </div>
  )
}

/**
 * Property condition section: overall, plumbing, electrical and foundation ratings,
 * plus HVAC, roof and water heater ages (MRG-1008).
 */
export function PropertyConditionSection({
  property,
  className,
}: PropertyConditionSectionProps) {
  const {
    year_built,
    overall_condition,
    overall_condition_notes,
    hvac_condition,
    hvac_condition_notes,
    roof_condition,
    roof_condition_notes,
    plumbing_condition,
    plumbing_condition_notes,
    electrical_condition,
    electrical_condition_notes,
    foundation_condition,
    foundation_condition_notes,
    hvac_install_year,
    hvac_install_year_unknown,
    roof_replacement_year,
    roof_replacement_year_unknown,
    water_heater_install_year,
    water_heater_install_year_unknown,
  } = property

  // Check if any condition data is available
  const hasConditions =
    overall_condition ||
    hvac_condition ||
    roof_condition ||
    plumbing_condition ||
    electrical_condition ||
    foundation_condition ||
    hvac_install_year ||
    hvac_install_year_unknown ||
    roof_replacement_year ||
    roof_replacement_year_unknown ||
    water_heater_install_year ||
    water_heater_install_year_unknown

  if (!hasConditions) {
    return null
  }

  return (
    <div className={clsx(styles.container, className)}>
      <Text as="h3" variant="body" weight="bold" className={styles.title}>Property Condition</Text>
      <div className={styles.conditionGrid}>
        <ConditionItem
          label="Overall"
          condition={overall_condition}
          notes={overall_condition_notes}
        />
        <ConditionItem
          label="Electrical"
          condition={electrical_condition}
          notes={electrical_condition_notes}
        />
        <ConditionItem
          label="Foundation"
          condition={foundation_condition}
          notes={foundation_condition_notes}
        />
        <ConditionItem
          label="Plumbing"
          condition={plumbing_condition}
          notes={plumbing_condition_notes}
        />
        <SystemAgeItem
          label="HVAC"
          year={hvac_install_year}
          unknown={hvac_install_year_unknown}
          yearBuilt={year_built}
          legacyCondition={hvac_condition}
          legacyNotes={hvac_condition_notes}
        />
        <SystemAgeItem
          label="Roof"
          year={roof_replacement_year}
          unknown={roof_replacement_year_unknown}
          yearBuilt={year_built}
          legacyCondition={roof_condition}
          legacyNotes={roof_condition_notes}
        />
        <SystemAgeItem
          label="Water heater"
          year={water_heater_install_year}
          unknown={water_heater_install_year_unknown}
          yearBuilt={year_built}
        />
      </div>
    </div>
  )
}
